import React, { useState } from 'react';
import { Deck, Card } from '../types';

interface AIDeckCreatorProps {
  onClose: () => void;
  onCreate: (deck: Omit<Deck, 'id' | 'createdAt' | 'modifiedAt'>) => void;
}

const AIDeckCreator: React.FC<AIDeckCreatorProps> = ({ onClose, onCreate }) => {
  const [deckDescription, setDeckDescription] = useState('');
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  const generatePrompt = () => {
    if (!deckDescription.trim()) {
      setError('Please enter a description for the deck');
      return;
    }

    const prompt = `Create a comprehensive flash card deck about "${deckDescription.trim()}". 

**CRITICAL: You must follow the exact JSON format and field requirements below.**

Generate the deck data in this exact JSON structure:

{
  "data": {
    "decks": [
      {
        "id": "temp-deck-id",
        "name": "Deck Name Here",
        "description": "Brief description of the deck",
        "createdAt": "${new Date().toISOString()}",
        "modifiedAt": "${new Date().toISOString()}",
        "categoryTags": ["tag1", "tag2"],
        "difficulty": "Easy",
        "targetLanguages": ["JavaScript"]
      }
    ],
    "cards": [
      {
        "id": "temp-card-id-1",
        "deckId": "temp-deck-id",
        "name": "Card Name",
        "question": "What is...?",
        "category": "Category Name",
        "difficulty": "Easy",
        "languages": {
          "JavaScript": "code example or answer here"
        },
        "notes": "Additional notes or explanation",
        "tags": ["tag1", "tag2"],
        "media": [],
        "createdAt": "${new Date().toISOString()}",
        "modifiedAt": "${new Date().toISOString()}"
      }
    ]
  }
}

**MANDATORY REQUIREMENTS - DO NOT DEVIATE:**

**Difficulty Levels:**
- ONLY use these exact values: "Easy", "Medium", "Hard"
- DO NOT use: "Beginner", "Intermediate", "Advanced" or any other values

**Required Fields (ALL must be included for each card):**
- id: unique identifier (string)
- deckId: must match the deck id (string)
- name: short card title (string)
- question: the question or prompt (string)
- category: category name (string)
- difficulty: ONLY "Easy", "Medium", or "Hard" (string)
- languages: object with language keys and answer values
- notes: additional explanation, can be empty string "" (string)
- tags: array of strings, can be empty []
- media: always empty array [] for now
- createdAt: timestamp string
- modifiedAt: timestamp string

**Content Guidelines:**
- Create 10-15 flash cards for this topic
- Use appropriate difficulty levels from the allowed values
- Include relevant programming languages in the languages object
- Make questions clear and specific
- Provide comprehensive answers in the languages object
- Add meaningful tags and categories
- Ensure all card IDs are unique within the deck
- All deck and card IDs should be descriptive but URL-safe (no spaces, special chars)

**JSON Format:**
- Use valid JSON syntax with proper quotes and commas
- Do not include any text before or after the JSON
- Ensure proper nesting and structure
- All string values must be in double quotes

Focus on creating high-quality educational content about ${deckDescription.trim()}.`;

    setGeneratedPrompt(prompt);
    setError('');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedPrompt);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = generatedPrompt;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setAiResponse(text);
      setError('');
    } catch (err) {
      setError('Unable to paste from clipboard. Please paste manually.');
    }
  };

  const validateAndImport = async () => {
    if (!aiResponse.trim()) {
      setError('Please paste the AI response first');
      return;
    }

    setIsValidating(true);
    setError('');

    try {
      // Parse the JSON response
      const importData = JSON.parse(aiResponse.trim());

      // Validate structure
      if (!importData || !importData.data || !importData.data.decks || !importData.data.cards) {
        throw new Error('Invalid format: Missing required data structure (data.decks and data.cards)');
      }

      const { decks, cards } = importData.data;

      if (!Array.isArray(decks) || decks.length === 0) {
        throw new Error('Invalid format: No decks found in data');
      }

      if (!Array.isArray(cards) || cards.length === 0) {
        throw new Error('Invalid format: No cards found in data');
      }

      const deck = decks[0];

      // Validate deck structure
      const requiredDeckFields = ['name', 'description', 'difficulty', 'targetLanguages', 'categoryTags'];
      for (const field of requiredDeckFields) {
        if (!(field in deck)) {
          throw new Error(`Invalid deck format: Missing required field '${field}'`);
        }
      }

      // Validate difficulty
      if (!['Easy', 'Medium', 'Hard'].includes(deck.difficulty)) {
        throw new Error('Invalid deck format: difficulty must be Easy, Medium, or Hard');
      }

      // Validate arrays
      if (!Array.isArray(deck.targetLanguages) || !Array.isArray(deck.categoryTags)) {
        throw new Error('Invalid deck format: targetLanguages and categoryTags must be arrays');
      }

      // Validate cards
      const requiredCardFields = ['name', 'question', 'category', 'difficulty', 'languages'];
      for (const card of cards) {
        for (const field of requiredCardFields) {
          if (!(field in card)) {
            throw new Error(`Invalid card format: Missing required field '${field}' in card '${card.name || 'unnamed'}'`);
          }
        }

        if (!['Easy', 'Medium', 'Hard'].includes(card.difficulty)) {
          throw new Error(`Invalid card format: difficulty must be Easy, Medium, or Hard in card '${card.name}'`);
        }

        if (typeof card.languages !== 'object' || card.languages === null) {
          throw new Error(`Invalid card format: languages must be an object in card '${card.name}'`);
        }

        // Ensure required array fields exist
        card.tags = card.tags || [];
        card.media = card.media || [];
        card.notes = card.notes || '';
      }

      // Create the deck manually since we have validated the data
      const deckToCreate: Omit<Deck, 'id' | 'createdAt' | 'modifiedAt'> = {
        name: deck.name,
        description: deck.description,
        difficulty: deck.difficulty,
        targetLanguages: deck.targetLanguages,
        categoryTags: deck.categoryTags
      };

      onCreate(deckToCreate);
      // Note: For now, this creates the deck structure only
      // The cards would need to be created through the deck editor or a future enhancement
      // to this feature could import cards as well

    } catch (parseError) {
      if (parseError instanceof SyntaxError) {
        setError('Invalid JSON format. Please check the AI response and ensure it\'s valid JSON.');
      } else {
        setError(parseError instanceof Error ? parseError.message : 'Validation failed');
      }
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Create Deck with AI</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded dark:bg-red-900 dark:text-red-100 dark:border-red-600">
              {error}
            </div>
          )}

          <div className="space-y-6">
            {/* Section 1: Describe the Deck */}
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">1. Describe the Deck</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Write a description for the deck of cards. For example, "Create a deck of cards that teaches morse code", or simply "morse code".
              </p>
              <textarea
                value={deckDescription}
                onChange={(e) => setDeckDescription(e.target.value)}
                className="form-textarea w-full h-24"
                placeholder="Enter your deck description..."
              />
              <button
                onClick={generatePrompt}
                disabled={!deckDescription.trim()}
                className="btn btn-primary"
              >
                Create Prompt
              </button>
            </div>

            {/* Section 2: Generated Prompt */}
            {generatedPrompt && (
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">2. Create the Cards</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Copy this prompt and paste it into your AI chatbot to generate the deck data.
                </p>
                <div className="relative">
                  <textarea
                    value={generatedPrompt}
                    readOnly
                    className="form-textarea w-full h-64 text-sm font-mono"
                  />
                  <button
                    onClick={copyToClipboard}
                    className="absolute top-2 right-2 btn btn-secondary text-sm"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}

            {/* Section 3: Import the Cards */}
            {generatedPrompt && (
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">3. Import the Cards</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Paste the output from your AI chatbot here and click Import to create your deck. This will create the deck structure and take you to the deck editor where you can review and add the generated cards.
                </p>
                <div className="flex gap-2 mb-2">
                  <button
                    onClick={pasteFromClipboard}
                    className="btn btn-secondary"
                  >
                    Paste
                  </button>
                </div>
                <textarea
                  value={aiResponse}
                  onChange={(e) => setAiResponse(e.target.value)}
                  className="form-textarea w-full h-32 text-sm font-mono"
                  placeholder="Paste the AI response here..."
                />
                <button
                  onClick={validateAndImport}
                  disabled={!aiResponse.trim() || isValidating}
                  className="btn btn-primary"
                >
                  {isValidating ? 'Validating...' : 'Import Deck'}
                </button>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIDeckCreator;